import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { extendDictionary, initLocalization } from '@/__core__/localization/localization'
import { formatPlayerName, preparePeriodsWithTitleAsNumber } from '@/__core__/helpers/Common.helpers'
import { HEADING_COLUMNS_WHITELIST, HEADING_FALLBACK_CELLS, TABLE_FALLBACK_DATA } from '../boxscore/BoxscoreWidget.constants'
import { allSettled } from '@/__core__/utils/fetch'
import { useLiveScoreboardStore } from '@/widgets/livescore/LiveScoreboard.store'
import { defineStore } from 'pinia'

const FALLBACK_STATISTICS = {
  home: {
    teamStatistics: {}
  },
  away: {
    teamStatistics: {}
  }
}

const TEAM_QUALIFIERS = ['home', 'away']

const shouldScheduleRefresh = initial => {
  const offset = 10 * 60 * 1000 // sinc with cache
  const currentTimeTs = Date.now()
  const eventStartTimeTs = new Date(initial.startTime).getTime() + offset
  const timeDifferenceTs = currentTimeTs - eventStartTimeTs
  const isBeforeGame = timeDifferenceTs >= -40 * 60 * 1000 && timeDifferenceTs <= 0

  return initial.isGameLive || isBeforeGame
}

/** @typedef {{ sportType: string, eventId: string, languageIdentifier: string, region: string }} Payload */
export const useStatisticsStore = defineStore('statistics', {
  state: () => ({
    statistics: null,
    initial: {
      headerData: {
        home: null,
        away: null
      },
      matchResultsPerPeriod: {
        perPeriod: []
      }
    },
    timeline: null,
    boxscore: null,
    drfRetryCounter: 0
  }),

  getters: {
    homeTeam: state => state.initial.headerData.home,
    awayTeam: state => state.initial.headerData.away,
    periods: state => state.initial.matchResultsPerPeriod.perPeriod,

    homeStats: state => state.statistics?.home?.teamStatistics,
    awayStats: state => state.statistics?.away?.teamStatistics,

    getPlayerStats: state => (teamQualifier, playerId) => state.statistics?.[teamQualifier]?.players?.find(player => player.personId === playerId),

    /** @returns {(teamQualifier: TeamQualifier, widgetSize: WidgetSize) => []} */
    getHeadings: state => (teamQualifier, widgetSize) => {
      const teamHeadings = state.boxscore[teamQualifier]?.heading
      if (!teamHeadings || !teamHeadings.length) {
        return HEADING_FALLBACK_CELLS[widgetSize]
      }

      return teamHeadings.reduce(
        (headings, headingItem) => {
          const shouldAddHeading = HEADING_COLUMNS_WHITELIST[widgetSize].includes(headingItem.property)
          const headingLabel = headingItem.shortLabel || headingItem.label

          shouldAddHeading && headings.push(headingLabel)

          return headings
        },
        [state.initial?.headerData?.[teamQualifier]?.name ?? '']
      )
    },

    /** @returns {(teamQualifier: TeamQualifier) => []} */
    getTableData: state => teamQualifier => {
      if (!state.boxscore[teamQualifier]?.tableData) {
        return TABLE_FALLBACK_DATA
      }

      return state.boxscore[teamQualifier].tableData.map(row => ({ ...row, name: formatPlayerName(row.name) }))
    },

    getInitial: state => state.initial,
    getTimeline: state => state.timeline,
    getHomeStats: state => state.statistics?.home,
    getAwayStats: state => state.statistics?.away,
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    },

    liveScoreboardStore() {
      return useLiveScoreboardStore()
    },
    resultHeader() {
      return this.liveScoreboardStore.header
    },
    adjustedTeamsLookup: state => {
      const output = {}
      const teamsLookup = state.initial.headerData

      for (const qualifier of TEAM_QUALIFIERS) {
        const team = teamsLookup[qualifier]

        output[team.teamId] = {
          qualifier,
          id: team.teamId,
          logo: team.logo,
          name: team.name,
          shortName: team.shortName
        }
      }

      return output
    },
    isFutureMatch() {
      return this.resultHeader?.upcoming
    },
    getCoverageCategory: state => widgetId => {
      if (state.isFutureMatch) {
        return 'future'
      }

      if (widgetId === 'game-stats') {
        return !state.statistics?.home && !state.statistics?.away ? 'limited' : ''
      } else if (widgetId === 'key-moments' && !state.timeline?.showMatchEvents) {
        return state.timeline.isGameLive ? 'updatable' : 'limited'
      } else {
        return ''
      }
    }
  },

  actions: {
    getShouldCallApi() {
      return shouldScheduleRefresh(this.initial)
    },
    /** @param {Payload} payload */
    async fetchStatisticsData(payload) {
      try {
        if (payload.sportType === 'football') {
          const [, statisticsData, timelineData] = await this.fetchFootballStats(payload)

          if (!statisticsData && !this.isFutureMatch) {
            throw new Error('Statistics data not found!')
          }

          this.updateStatistics(statisticsData, statisticsData?.initial, timelineData)
        } else if (payload.sportType === 'basketball') {
          const [, localizationData, statisticsData] = await this.fetchBaskeballStats(payload)

          if (!localizationData) {
            throw new Error('Localization data not found')
          }

          extendDictionary(localizationData || {}, payload.languageIdentifier)

          if (!statisticsData) {
            throw new Error('Statistics data not found')
          }

          this.updateStatistics(statisticsData, statisticsData?.initial)
        }
        return false
      } catch (error) {
        throw new Error(`[Statistics.store, fetchStatisticsData]: ${error.message}`)
      }
    },

    /** @param {Payload} payload */
    async fetchFootballStats(payload) {
      return allSettled([
        initLocalization(payload.languageIdentifier),
        SportsModeServiceRepository.Statistics.get(payload),
        SportsModeServiceRepository.Timeline.get(payload),
        this.liveScoreboardStore.fetchLiveScoreboardData(payload)
      ]).then(responses => responses.map(response => response?.data))
    },

    /** @param {Payload} payload */
    async fetchBaskeballStats(payload) {
      return allSettled([
        initLocalization(payload.languageIdentifier),
        SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'statistics' }),
        SportsModeServiceRepository.Statistics.get(payload)
      ]).then(responses => responses.map(response => response?.data))
    },

    updateStatistics(statisticsData = {}, initialData = {}, timelineData = {}) {
      if (initialData.sportType === 'basketball') {
        this.initial = {
          ...initialData,
          matchResultsPerPeriod: {
            perPeriod: preparePeriodsWithTitleAsNumber(initialData)
          }
        }
      } else {
        this.initial = initialData
      }

      this.statistics = statisticsData?.statistics || FALLBACK_STATISTICS
      this.boxscore = statisticsData?.boxScore || []
      this.timeline = timelineData || []
    },
    /** @param {Payload} payload */
    async scheduleStatisticsDataRefresh(payload) {
      try {
        if (payload.sportType === 'football') {
          const [statisticsData, timelineData] = await allSettled([SportsModeServiceRepository.Statistics.get(payload), SportsModeServiceRepository.Timeline.get(payload)]).then(
            responses => responses.map(response => response?.data)
          )

          this.updateStatistics(statisticsData, statisticsData?.initial, timelineData)
        }
      } catch {
        this.drfRetryCounter++
      }
    }
  }
})
